/**
 * DocumentIcons Component
 *
 * Displays document icons with tooltips for rebate agreements.
 * Shows file icons with tooltips containing file names.
 * Supports both single and multiple document display modes.
 */

import React from 'react';
import { FileTextIcon, FileIcon } from '@phosphor-icons/react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface DocumentIconsProps {
  documents: Array<{
    id: string;
    name: string;
    storage_path: string;
  }>;
  onDocumentClick?: (documentId: string) => void;
  mode?: 'icons' | 'list' | 'compact';
  maxVisible?: number;
  className?: string;
  showTooltip?: boolean;
}

const getFileIcon = (fileName: string) => {
  const extension = fileName.toLowerCase().split('.').pop();

  switch (extension) {
    case 'pdf':
      return <FileTextIcon className="w-4 h-4 text-blue-600" />;
    case 'doc':
    case 'docx':
      return <FileTextIcon className="w-4 h-4 text-blue-600" />;
    case 'xls':
    case 'xlsx':
      return <FileTextIcon className="w-4 h-4 text-green-600" />;
    default:
      return <FileIcon className="w-4 h-4 text-gray-600" />;
  }
};

const DocumentIcons: React.FC<DocumentIconsProps> = ({
  documents,
  onDocumentClick,
  mode = 'icons',
  maxVisible = 3,
  className = '',
  showTooltip = true,
}) => {
  if (!documents || documents.length === 0) {
    return (
      <span className={`text-sm text-muted-foreground ${className}`}>
        No documents
      </span>
    );
  }

  const visibleDocuments = documents.slice(0, maxVisible);
  const remainingCount = documents.length - maxVisible;

  if (mode === 'list') {
    return (
      <div className={`space-y-1 ${className}`}>
        {documents.map((doc) => {
          const button = (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDocumentClick?.(doc.id)}
              className="h-auto p-1 justify-start text-left"
            >
              {getFileIcon(doc.name)}
              <span className="ml-2 truncate max-w-[200px]">{doc.name}</span>
            </Button>
          );

          if (!showTooltip) {
            return <div key={doc.id}>{button}</div>;
          }

          return (
            <TooltipProvider key={doc.id}>
              <Tooltip>
                <TooltipTrigger asChild>{button}</TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs break-words">{doc.name}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        })}
      </div>
    );
  }

  if (mode === 'compact') {
    return (
      <div className={`flex items-center gap-1 ${className}`}>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 p-2 flex items-center gap-1 hover:bg-accent"
          onClick={(e) => {
            e.stopPropagation();
            onDocumentClick?.(documents[0]?.id || '');
          }}
        >
          {getFileIcon(documents[0]?.name || '')}
          <span className="text-sm font-medium">{documents.length}</span>
        </Button>
      </div>
    );
  }

  // Default 'icons' mode
  return (
    <div className={`flex items-center gap-1 ${className}`}>
      {visibleDocuments.map((doc) => {
        const button = (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onDocumentClick?.(doc.id)}
            className="h-8 w-8 p-0"
          >
            {getFileIcon(doc.name)}
          </Button>
        );

        if (!showTooltip) {
          return <div key={doc.id}>{button}</div>;
        }

        return (
          <TooltipProvider key={doc.id}>
            <Tooltip>
              <TooltipTrigger asChild>{button}</TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs break-words">{doc.name}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      })}

      {remainingCount > 0 && (
        <>
          {showTooltip ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="secondary" className="text-xs">
                    +{remainingCount}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="max-w-xs">
                    <p className="font-medium mb-1">Additional documents:</p>
                    <ul className="text-sm space-y-1">
                      {documents.slice(maxVisible).map((doc) => (
                        <li key={doc.id} className="break-words">
                          • {doc.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            <Badge variant="secondary" className="text-xs">
              +{remainingCount}
            </Badge>
          )}
        </>
      )}
    </div>
  );
};

export default DocumentIcons;
